<script lang="ts">
  import type { LayoutPreference, Runner, RunnerTopPrices } from '$types/races';
  import { isVacantBox, selectedRaceHasSP, type MarketMap } from '$utils/race';
  import { readable } from 'svelte/store';
  import BestPriceCell from './BestPriceCell.svelte';
  import BetfairCell from './BetfairCell.svelte';
  import BetfairSpCell from './BetfairSPCell.svelte';
  import PriceCell from './PriceCell.svelte';
  import PriceFlucCell from './PriceFlucCell.svelte';
  import RunnerInfoCell from './RunnerInfoCell.svelte';
  import ScratchedRunnerInfo from './ScratchedRunnerInfo.svelte';
  import TextInputCell from './TextInputCell.svelte';

  export let runner: Runner;
  export let marketMapping: MarketMap;
  export let runnerTopPrices: RunnerTopPrices | undefined;
  export let betfairMarketTypes: ('win' | 'place')[];

  export let computedLayoutPreferences: LayoutPreference[];
  $: ({ fixedWin: fixedWinTopPrices, fixedPlace: fixedPlaceTopPrices } =
    runnerTopPrices ?? {
      fixedWin: readable([]),
      fixedPlace: readable([])
    });

  let winTopPrice: (typeof $fixedWinTopPrices)[number] =
    $fixedWinTopPrices?.[0];
  let placeTopPrice: (typeof $fixedPlaceTopPrices)[number] =
    $fixedPlaceTopPrices?.[0];
  $: {
    // avoid flickering of the top prices
    // only update the top prices if they have changed
    if (
      winTopPrice?.fixedWin.price !== $fixedWinTopPrices?.[0]?.fixedWin.price
    ) {
      winTopPrice = $fixedWinTopPrices?.[0];
    }
    if (
      placeTopPrice?.fixedPlace.price !==
      $fixedPlaceTopPrices?.[0]?.fixedPlace.price
    ) {
      placeTopPrice = $fixedPlaceTopPrices?.[0];
    }
  }

  // calculate the top 3 prices for each market
  $: topPrices = {
    fixedWin: $fixedWinTopPrices.map((p) => p.fixedWin.price),
    fixedPlace: $fixedPlaceTopPrices.map((p) => p.fixedPlace.price)
  } as {
    fixedWin: number[];
    fixedPlace: number[];
  };
</script>

<RunnerInfoCell {runner} />
<!-- show sp first if there is one -->
{#if $selectedRaceHasSP}
  {#each betfairMarketTypes as betfairMarketType}
    {@const market = runner.betfairMarkets?.find(
      (m) => m.marketName === betfairMarketType
    )}
    {@const marketStore = market
      ? marketMapping.getBetfairMarket(market.id)
      : undefined}
    <BetfairSpCell {marketStore} />
  {/each}
{/if}

<!-- if the runner is scratched, show their scratched status -->
{#if runner.scratchedTime || isVacantBox(runner)}
  <ScratchedRunnerInfo {runner} />
{:else}
  {#each computedLayoutPreferences as layoutPreference}
    {@const bookmakerMarketType =
      layoutPreference.type === 'FIXED_WIN' ? 'fixedWin' : 'fixedPlace'}
    {#if runner.scratchedTime}
      <td />
    {:else if layoutPreference.type === 'BEST_PRICE_WIN' || layoutPreference.type === 'BEST_PRICE_PLACE'}
      {@const isFixedWin = layoutPreference.type === 'BEST_PRICE_WIN'}
      {@const bestPrice = isFixedWin ? winTopPrice : placeTopPrice}
      {#key bestPrice}
        <BestPriceCell {bestPrice} {layoutPreference} {runner} />
      {/key}
    {:else if layoutPreference.bookmaker === 'Betfair'}
      {#if (!$selectedRaceHasSP || layoutPreference.type === 'FIXED_WIN_FLUCS' || layoutPreference.type === 'FIXED_PLACE_FLUCS') && !isVacantBox(runner) && !runner.scratchedTime}
        {@const betfairMarketType =
          layoutPreference.type === 'FIXED_WIN' ||
          layoutPreference.type === 'FIXED_WIN_FLUCS'
            ? 'win'
            : 'place'}
        {@const market = runner.betfairMarkets?.find(
          (m) => m.marketName === betfairMarketType
        )}
        {@const marketStore =
          market && marketMapping.getBetfairMarket(market.id)}
        {#if layoutPreference.type === 'FIXED_WIN_FLUCS' || layoutPreference.type === 'FIXED_PLACE_FLUCS'}
          <PriceFlucCell betfairMarket={marketStore} {layoutPreference} />
        {:else}
          <BetfairCell {layoutPreference} {marketStore} />
        {/if}
      {/if}
    {:else if !isVacantBox(runner) && !runner.scratchedTime}
      {@const market = runner.bookmakerMarkets?.find(
        (m) => m.bookmaker === layoutPreference.bookmaker
      )}
      {@const marketStore = market
        ? marketMapping.getBookmakerMarket(market.id)
        : undefined}
      {#if layoutPreference.type === 'FIXED_WIN' || layoutPreference.type === 'FIXED_PLACE'}
        <PriceCell
          {runner}
          market={marketStore}
          marketType={bookmakerMarketType}
          topPrices={topPrices[bookmakerMarketType]}
        />
      {:else if layoutPreference.type === 'FIXED_WIN_FLUCS' || layoutPreference.type === 'FIXED_PLACE_FLUCS'}
        <PriceFlucCell market={marketStore} {layoutPreference} />
      {:else if layoutPreference.type === 'TEXT'}
        <TextInputCell {runner} {layoutPreference} {marketMapping} />
      {:else}
        <td class="" />
      {/if}
    {/if}
  {/each}
{/if}
